import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { User } from "lucide-react";
import axios from "axios";
import { Toaster } from "sonner";
import { trackStep } from "./utils/funnel";
import { API, withoutIllustrations } from "./utils/constants";
import { ThemeToggle } from "./components/shared";
import AdminPanel from "./AdminPanel";
import LandingPage from "./pages/LandingPage";
import DatesPage from "./pages/DatesPage";
import CitiesPage from "./pages/CitiesPage";
import TravelerPage from "./pages/TravelerPage";
import StylePage from "./pages/StylePage";
import VibesPage from "./pages/VibesPage";
import InputPage from "./pages/InputPage";
import SummaryPage from "./pages/SummaryPage";
import CatalogPage from "./pages/CatalogPage";
import ResultPage from "./pages/ResultPage";
import CompanionPage from "./pages/CompanionPage";
import AuthModal from "./pages/AuthModal";
import FeedbackButton from "./components/FeedbackButton";
import MyTripsPage from "./pages/MyTripsPage";
import PricingPage from "./pages/PricingPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import RefundPolicyPage from "./pages/RefundPolicyPage";
import AgendaPage, { AgendaShape } from "./pages/AgendaPage";
import { placeKey, toPlaceOf } from "./utils/agenda";
import AGENDA_DEMO from "./pages/agendaDemoData";
import QuestionFlow from "./components/QuestionFlow";
import PracticalPage from "./pages/PracticalPage";
import { useTripDraft, readLocalTrip, clearLocalTrip, tripStarted, titleOf, DRAFT_STEPS } from "./utils/tripDraft";
import QUESTIONS_DEMO from "./pages/questionsDemoData";
import Footer from "./components/Footer";

// ── Trip-state persistence across external redirects (Stripe checkout) ──
// Everything the user has built lives in React state, which dies on the
// full-page reload when they return from Stripe. Before this, a customer
// who PAID came back to an empty app — trip gone at the exact moment of
// highest trust. One sessionStorage snapshot fixes it: same tab, survives
// the redirect, gone when the tab closes (no stale-state risk next visit).
const TRIP_STATE_KEY = "ju_tripState";

function loadTripState() {
  try {
    return JSON.parse(sessionStorage.getItem(TRIP_STATE_KEY) || "null") || {};
  } catch {
    return {};
  }
}

// a reload on the agenda page: the agenda lives in memory only, so back to the places it was planned from
function AgendaLost({ onLost }) {
  useEffect(() => { onLost(); }, [onLost]);
  return null;
}

function App() {
  const [isAdmin, setIsAdmin] = useState(() => window.location.hash === "#admin" || window.location.pathname === "/admin");
  // the agenda step, as a prototype with sample data (#agenda-demo) — not part of the flow yet
  const [isAgendaDemo, setIsAgendaDemo] = useState(() => window.location.hash === "#agenda-demo");
  // the questions asked before choosing, with sample questions (#questions-demo) — to look at without a reading
  const [isQuestionsDemo, setIsQuestionsDemo] = useState(() => window.location.hash === "#questions-demo");
  const restored = useRef(loadTripState()).current;
  const [currentPage, setCurrentPage] = useState(() => {
    // Restore the current page after a refresh OR an external redirect
    // (Stripe checkout). history.state survives a reload; ju_currentPage
    // survives a full navigation. Reading history first means a plain
    // refresh keeps the user on the page they were on (e.g. their freshly
    // generated Result) instead of dumping them back to the landing page.
    try {
      const fromHistory = window.history.state?.page;
      if (fromHistory && fromHistory !== "landing") return fromHistory;
      const saved = sessionStorage.getItem("ju_currentPage");
      if (saved && saved !== "landing") return saved;
    } catch {}
    return "landing";
  });
  const [dates, setDates] = useState(restored.dates || { startDate: "", endDate: "", arrivalTime: "14:00", departureTime: "18:00", airport: "NRT" });
  const [selectedCities, setSelectedCities] = useState(restored.selectedCities || []);
  const [userInput, setUserInput] = useState(restored.userInput || {});
  const [extractedPlaces, setExtractedPlaces] = useState(restored.extractedPlaces || []);
  const [selectedPlaces, setSelectedPlaces] = useState(restored.selectedPlaces || []);
  const [selectedVibes, setSelectedVibes] = useState(restored.selectedVibes || []);
  const [tripPace, setTripPace] = useState(restored.tripPace || "balanced"); // "intensive" | "balanced" | "relaxed"
  const [travelerType, setTravelerType] = useState(restored.travelerType || "");
  const [kidAges, setKidAges] = useState(restored.kidAges || []);
  const [isFirstTimer, setIsFirstTimer] = useState(restored.isFirstTimer ?? null);
  const [dayStartLabel, setDayStartLabel] = useState(restored.dayStartLabel || "");
  const [dayEndLabel, setDayEndLabel] = useState(restored.dayEndLabel || "");
  const [tripConfig, setTripConfigRaw] = useState(() => {
    const t = restored.tripConfig || {};
    return t.itinerary ? { ...t, itinerary: withoutIllustrations(t.itinerary) } : t;
  });
  // every trip enters through here — generated, opened from My Trips, re-planned — so one rule covers them all
  const setTripConfig = useCallback((next) => setTripConfigRaw(prev => {
    const value = typeof next === "function" ? next(prev) : next;
    return value && value.itinerary ? { ...value, itinerary: withoutIllustrations(value.itinerary) } : value;
  }), []);

  // Pipeline state (persists across navigation so selection survives catalog round-trip)
  const [profilerBrief, setProfilerBrief] = useState(restored.profilerBrief || null);
  const [curatorBrief, setCuratorBrief] = useState(restored.curatorBrief || null);
  // AI-derived state lifted from SummaryPage so it survives Catalog round-trips
  // without re-firing the heavy preview Selector call (Class 5 of the
  // architecture review). Inputs unchanged + we already have analyses =>
  // SummaryPage useEffect noops; user sees the same picks they left with.
  const [aiAnalyses, setAiAnalyses] = useState(restored.aiAnalyses || {});
  const [previewPicks, setPreviewPicks] = useState(restored.previewPicks || []);
  const [dayBrief, setDayBrief] = useState(restored.dayBrief || null);
  // what they answered when we asked before choosing (backend core/questions)
  const [questionAnswers, setQuestionAnswers] = useState(restored.questionAnswers || {});
  // the practical page: diet, allergies, needs, what to skip, how they eat (backend core/needs)
  const [practical, setPractical] = useState(restored.practical || {});
  // the trip in the making this work belongs to (backend core/drafts), kept in their account when signed in
  const [draftId, setDraftId] = useState(restored.draftId || null);

  // Snapshot the trip state whenever it changes. try/catch guards quota
  // errors (a full itinerary is ~200KB; sessionStorage allows ~5MB).
  useEffect(() => {
    try {
      sessionStorage.setItem(TRIP_STATE_KEY, JSON.stringify({
        dates, selectedCities, userInput, extractedPlaces, selectedPlaces,
        selectedVibes, tripPace, travelerType, kidAges, isFirstTimer,
        dayStartLabel, dayEndLabel, tripConfig,
        profilerBrief, curatorBrief, aiAnalyses, previewPicks, dayBrief, questionAnswers, practical, draftId,
      }));
    } catch { /* quota or private-mode — degrade to pre-persistence behavior */ }
  }, [dates, selectedCities, userInput, extractedPlaces, selectedPlaces,
      selectedVibes, tripPace, travelerType, kidAges, isFirstTimer,
      dayStartLabel, dayEndLabel, tripConfig,
      profilerBrief, curatorBrief, aiAnalyses, previewPicks, dayBrief, questionAnswers, practical, draftId]);
  // Auth state
  const [userEmail, setUserEmail] = useState(() => localStorage.getItem("ju_email") || localStorage.getItem("jf_email") || "");
  const [sessionToken, setSessionToken] = useState(() => localStorage.getItem("jf_session") || "");
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [pendingPage, setPendingPage] = useState(null);      // where they were going when we asked for their email
  const signInFirst = useRef(false);                         // the server says whether an email comes before a result
  const [agendaStep, setAgendaStep] = useState(false);        // ... and whether the days are shaped before they are written
  const [agendaRun, setAgendaRun] = useState(null);           // the agenda the server planned (or, free, its shape)
  // two views of one trip (owner, 2026-11-04): the glance says what is in it, the calendar when; both edit it
  const [agendaSaved, setAgendaSaved] = useState(null);       // the calendar as they left it: { days, tray }
  const [writeNow, setWriteNow] = useState(false);            // "Generate itinerary" on the calendar
  const selectedKeys = useMemo(() => new Set((selectedPlaces || []).map((n) => String(n).toLowerCase())), [selectedPlaces]);
  const listedKeys = useMemo(() => new Set((extractedPlaces || []).map((p) => placeKey(p))), [extractedPlaces]);
  // new places chosen for them (another reading of the wishlist): the calendar is laid out again from those
  useEffect(() => { setAgendaRun(null); setAgendaSaved(null); }, [previewPicks]);
  const [payNow, setPayNow] = useState(false);                // free, "Unfold my days": the plans open on the selection page
  const [refundAckText, setRefundAckText] = useState("");     // what they tick before their first calendar (the server's words)
  useEffect(() => {
    axios.get(`${API}/auth/policy`).then(r => {
      signInFirst.current = !!r.data?.sign_in_first;
      setAgendaStep(!!r.data?.agenda_step);
      setRefundAckText(r.data?.refund_ack_text || "");
    }).catch(() => {});
  }, []);
  const [pendingSaveAfterAuth, setPendingSaveAfterAuth] = useState(false);

  // Payment state
  const [userCredits, setUserCredits] = useState(null);
  const [hasCompanion, setHasCompanion] = useState(false);
  const [paymentToast, setPaymentToast] = useState(null);

  const isPopstateNav = useRef(false);

  const navigateTo = useCallback((page) => {
    // no result without an email (owner, 2026-10-27): the selection opens for a signed-in traveller
    if (page === "summary" && signInFirst.current && !localStorage.getItem("jf_session")) {
      setPendingPage("summary");
      setShowAuthModal(true);
      return;
    }
    trackStep(page);            // where people leave (utils/funnel)
    setCurrentPage(page);
    window.scrollTo(0, 0);
    if (!isPopstateNav.current) {
      window.history.pushState({ page }, "", window.location.pathname);
    }
    isPopstateNav.current = false;
    // Persist for recovery after external redirects (Stripe checkout)
    try { sessionStorage.setItem("ju_currentPage", page); } catch {}
  }, []);

  // Replace initial history entry with actual starting page
  useEffect(() => {
    const initialPage = sessionStorage.getItem("ju_currentPage") || "landing";
    trackStep(initialPage);
    window.history.replaceState({ page: initialPage }, "", window.location.pathname);
  }, []);

  // Browser back/forward button support
  useEffect(() => {
    const onPopState = (e) => {
      const page = e.state?.page || "landing";
      isPopstateNav.current = true;
      setCurrentPage(page);
      window.scrollTo(0, 0);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  // Handle payment return from Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const paymentSuccess = params.get("payment_success");
    const paymentCancelled = params.get("payment_cancelled");

    if (paymentSuccess && sessionId) {
      // Clean URL, preserve restored page in history state
      const restoredPage = sessionStorage.getItem("ju_currentPage") || "pricing";
      window.history.replaceState({ page: restoredPage }, "", window.location.pathname);
      // Poll payment status
      pollPaymentStatus(sessionId);
    } else if (paymentCancelled) {
      const restoredPage = sessionStorage.getItem("ju_currentPage") || "pricing";
      window.history.replaceState({ page: restoredPage }, "", window.location.pathname);
      setPaymentToast({ type: "info", message: "Payment cancelled. Your trip is still saved." });
      setTimeout(() => setPaymentToast(null), 4000);
    }
  }, []);

  // Complete magic-link sign-in when the emailed link is opened. In
  // production the backend emails FRONTEND_URL?magic_token=<token>; this
  // reads that param, exchanges it for a session, and cleans the URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const magicToken = params.get("magic_token");
    if (!magicToken) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/verify/${magicToken}`);
        setUserEmail(res.data.email);
        setSessionToken(res.data.session_token);
        localStorage.setItem("jf_email", res.data.email);
        localStorage.setItem("ju_email", res.data.email);
        localStorage.setItem("jf_session", res.data.session_token);
        setPaymentToast({ type: "success", message: "You're signed in." });
        setTimeout(() => setPaymentToast(null), 4000);
      } catch (e) {
        setPaymentToast({ type: "error", message: "That sign-in link has expired. Please request a new one." });
        setTimeout(() => setPaymentToast(null), 6000);
      } finally {
        const url = new URL(window.location.href);
        url.searchParams.delete("magic_token");
        window.history.replaceState(window.history.state, "", url.pathname + url.search);
      }
    })();
  }, []);

  const pollPaymentStatus = async (sessionId, attempt = 0) => {
    // ~90s of polling (30 × 3s). Stripe settlement + webhook can take well
    // over the old 16s window, so a real, successful payment was being
    // reported to the buyer as a failure.
    if (attempt >= 30) {
      setPaymentToast({ type: "info", message: "Still confirming your payment — your credits will appear shortly. Refresh in a minute if you don't see them." });
      setTimeout(() => setPaymentToast(null), 7000);
      return;
    }
    try {
      const res = await axios.get(`${API}/payment/status/${sessionId}`);
      if (res.data.payment_status === "paid") {
        setPaymentToast({ type: "success", message: `Payment received: ${res.data.credits_added} ${res.data.credits_added === 1 ? "trip" : "trips"} added to your account.` });
        // Refresh credits
        const email = localStorage.getItem("ju_email") || userEmail;
        if (email) {
          const cRes = await axios.get(`${API}/credits/check?email=${encodeURIComponent(email)}`);
          setUserCredits(cRes.data.credits);
          setHasCompanion(cRes.data.has_companion || false);
        }
        // If they paid to unfold a teaser trip, complete the unlock now —
        // the Stripe session id is the proof of payment. The trip unfolds
        // the moment they land back, no extra taps.
        const lockedId = tripConfig?.itinerary?.locked ? tripConfig.itinerary.id : null;
        if (lockedId) {
          try {
            const u = await axios.post(`${API}/itinerary/${lockedId}/unlock`, { stripe_session_id: sessionId });
            setTripConfig(prev => ({ ...(prev || {}), itinerary: u.data }));
            setPaymentToast({ type: "success", message: "Your full trip is unfolded. Enjoy Japan!" });
          } catch (ue) {
            console.error("Unlock after payment failed", ue);
            setPaymentToast({ type: "info", message: "Payment confirmed — tap 'Unfold my full trip' to open your itinerary." });
          }
        }
        setTimeout(() => setPaymentToast(null), 5000);
      } else if (res.data.status === "expired") {
        setPaymentToast({ type: "error", message: "Payment expired. Please try again." });
        setTimeout(() => setPaymentToast(null), 5000);
      } else {
        // Still processing, poll again
        setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), 3000);
      }
    } catch (e) {
      setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), 2000);
    }
  };

  useEffect(() => {
    const onHash = () => {
      setIsAdmin(window.location.hash === "#admin" || window.location.pathname === "/admin");
      setIsAgendaDemo(window.location.hash === "#agenda-demo");
      setIsQuestionsDemo(window.location.hash === "#questions-demo");
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  // Validate session on mount
  useEffect(() => {
    if (sessionToken) {
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${sessionToken}` } })
        .then(res => setUserEmail(res.data.email))
        .catch(() => { setSessionToken(""); localStorage.removeItem("jf_session"); });
    }
  }, []);

  // Load credits on mount if email exists
  useEffect(() => {
    if (userEmail) {
      axios.get(`${API}/credits/check?email=${encodeURIComponent(userEmail)}`)
        .then(res => { setUserCredits(res.data.credits); setHasCompanion(res.data.has_companion || false); })
        .catch(() => { console.warn("Failed to check credits"); });
    }
  }, [userEmail]);

  const handleSetUserEmail = useCallback((email) => {
    setUserEmail(email);
    localStorage.setItem("ju_email", email);
  }, []);

  const handleAuthenticated = (email, token) => {
    setUserEmail(email);
    setSessionToken(token);
    localStorage.setItem("jf_email", email);
    localStorage.setItem("ju_email", email);
    localStorage.setItem("jf_session", token);
    if (pendingSaveAfterAuth) {
      setPendingSaveAfterAuth(false);
      saveCurrentTrip(token);
    }
    if (pendingPage) {
      const page = pendingPage;
      setPendingPage(null);
      navigateTo(page);
    }
  };

  const handleLogout = () => {
    if (sessionToken) axios.post(`${API}/auth/logout`, {}, { headers: { Authorization: `Bearer ${sessionToken}` } }).catch(() => { /* best-effort */ });
    setUserEmail("");
    setSessionToken("");
    setUserCredits(null);
    localStorage.removeItem("jf_email");
    localStorage.removeItem("ju_email");
    localStorage.removeItem("jf_session");
    navigateTo("landing");
  };

  const saveCurrentTrip = async (tokenOverride) => {
    const token = tokenOverride || sessionToken;
    if (!token) { setPendingSaveAfterAuth(true); setShowAuthModal(true); return; }
    const itin = tripConfig?.itinerary;
    if (!itin) return;
    try {
      await axios.post(`${API}/itineraries/save`, { itinerary: itin, title: itin.title }, { headers: { Authorization: `Bearer ${token}` } });
      setPaymentToast({ type: "success", message: "Trip saved to My Trips." });
      setTimeout(() => setPaymentToast(null), 4000);
    } catch (e) {
      if (e.response?.status === 401) { setPendingSaveAfterAuth(true); setShowAuthModal(true); }
      else { setPaymentToast({ type: "error", message: "Couldn't save your trip — please try again." }); setTimeout(() => setPaymentToast(null), 5000); }
    }
  };

  const handleLoadTrip = (itineraryData) => {
    setTripConfig({ itinerary: itineraryData });
    navigateTo("result");
  };

  // ── The trip in the making, saved as they go (utils/tripDraft, backend core/drafts) ──
  const draftInputs = useMemo(() => ({
    dates, selectedCities, travelerType, kidAges, isFirstTimer, dayStartLabel, dayEndLabel,
    tripPace, selectedVibes, practical, userInput, questionAnswers,
  }), [dates, selectedCities, travelerType, kidAges, isFirstTimer, dayStartLabel, dayEndLabel,
      tripPace, selectedVibes, practical, userInput, questionAnswers]);
  const draftSelection = useMemo(() => ({ selectedPlaces, extractedPlaces }), [selectedPlaces, extractedPlaces]);
  const draftPreview = useMemo(() => ({ profilerBrief, curatorBrief, aiAnalyses, previewPicks, dayBrief }),
    [profilerBrief, curatorBrief, aiAnalyses, previewPicks, dayBrief]);
  useTripDraft({ sessionToken, draftId, setDraftId, step: currentPage, inputs: draftInputs,
    selection: draftSelection, preview: draftPreview });

  // Open a trip in the making where they left it: what they told us, what they kept, the last preview (no new reading).
  const openDraft = (draft) => {
    const i = draft?.inputs || {};
    setDates(i.dates || { startDate: "", endDate: "", arrivalTime: "14:00", departureTime: "18:00", airport: "NRT" });
    setSelectedCities(i.selectedCities || []);
    setTravelerType(i.travelerType || "");
    setKidAges(i.kidAges || []);
    setIsFirstTimer(i.isFirstTimer ?? null);
    setDayStartLabel(i.dayStartLabel || "");
    setDayEndLabel(i.dayEndLabel || "");
    setTripPace(i.tripPace || "balanced");
    setSelectedVibes(i.selectedVibes || []);
    setPractical(i.practical || {});
    setUserInput(i.userInput || {});
    setQuestionAnswers(i.questionAnswers || {});
    const sel = draft?.selection || {}, pv = draft?.preview || {};
    setSelectedPlaces(sel.selectedPlaces || []);
    setExtractedPlaces(pv.profilerBrief ? (sel.extractedPlaces || []) : []);
    setProfilerBrief(pv.profilerBrief || null);
    setCuratorBrief(pv.curatorBrief || null);
    setAiAnalyses(pv.aiAnalyses || {});
    setPreviewPicks(pv.previewPicks || []);
    setDayBrief(pv.dayBrief || null);
    setTripConfig({});
    setDraftId(draft?.draft_id || null);
    const step = DRAFT_STEPS.includes(draft?.step) ? draft.step : "dates";
    navigateTo(step === "summary" && !pv.profilerBrief ? "input" : step);
  };
  const localTrip = currentPage === "landing" ? readLocalTrip() : null;

  const startOver = useCallback(() => {
    setCurrentPage("landing");
    // a new trip starts from nothing the last one chose (2026-10-07: the traveller type, kids' ages, first
    // time, the day's hours stayed; the airport was dropped)
    setDates({ startDate: "", endDate: "", arrivalTime: "14:00", departureTime: "18:00", airport: "NRT" });
    setTravelerType("");
    setKidAges([]);
    setIsFirstTimer(null);
    setDayStartLabel("");
    setDayEndLabel("");
    setSelectedCities([]);
    setUserInput({});
    setExtractedPlaces([]);
    setSelectedPlaces([]);
    setSelectedVibes([]);
    setTripPace("balanced");
    setTripConfig({});
    // Clear the pipeline state too — a fresh trip must not inherit the
    // previous trip's picks, brief, or insights.
    setProfilerBrief(null);
    setCuratorBrief(null);
    setAiAnalyses({});
    setPreviewPicks([]);
    setDayBrief(null);
    setQuestionAnswers({});
    setPractical({});
    setDraftId(null);
    clearLocalTrip();
    try {
      sessionStorage.removeItem(TRIP_STATE_KEY);
      sessionStorage.removeItem("ju_currentPage");
      sessionStorage.removeItem("ju_help_suggestion");      // a new trip, a new proposal
    } catch {}
    window.scrollTo(0, 0);
  }, [setTripConfig]);

  if (isAdmin) return <AdminPanel />;
  if (isAgendaDemo) return <><AgendaPage data={AGENDA_DEMO} /><ThemeToggle /></>;
  if (isQuestionsDemo) return (
    <QuestionFlow questions={QUESTIONS_DEMO}
      onDone={(given) => { console.info("[questions-demo] answers", given); window.location.hash = ""; }} />
  );

  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      <ThemeToggle />

      {/* Payment toast */}
      <AnimatePresence>
        {paymentToast && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-4 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-xl text-sm font-medium shadow-lg"
            style={{
              background: paymentToast.type === "success" ? "var(--cat-culture-solid)" : paymentToast.type === "error" ? "var(--cat-nightlife-solid)" : "var(--zen-accent)",
              color: "#fff",
            }}
            data-testid="payment-toast"
          >
            {paymentToast.message}
          </motion.div>
        )}
      </AnimatePresence>

      {/* User indicator */}
      {userEmail && currentPage !== "mytrips" && (
        <div className="fixed top-4 right-16 z-40">
          <button onClick={() => navigateTo("mytrips")}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-all hover:opacity-80"
            style={{background:'var(--zen-s1)',border:'1px solid var(--zen-border)',color:'var(--zen-sub)'}}
            data-testid="user-indicator">
            <User className="w-3 h-3" /> My Trips
          </button>
        </div>
      )}

      <FeedbackButton page={currentPage} draftId={draftId} signedIn={!!sessionToken} />
      <AuthModal isOpen={showAuthModal} gate={!!pendingPage} onClose={() => { setShowAuthModal(false); setPendingSaveAfterAuth(false); setPendingPage(null); }} onAuthenticated={handleAuthenticated} />

      {currentPage === "landing" && (
        <LandingPage onStart={() => navigateTo("dates")} userEmail={userEmail} onMyTrips={() => navigateTo("mytrips")} onPricing={() => navigateTo("pricing")}
          resumeTitle={localTrip && tripStarted(localTrip.inputs) ? titleOf(localTrip.inputs) : ""}
          onResume={() => openDraft({ ...localTrip, draft_id: localTrip?.draftId || null })} />
      )}

      {currentPage === "mytrips" && (
        <MyTripsPage userEmail={userEmail} sessionToken={sessionToken} onLoadTrip={handleLoadTrip} onBack={() => navigateTo("landing")} onLogout={handleLogout}
          onOpenDraft={openDraft} />
      )}

      {currentPage === "pricing" && (
        <PricingPage onBack={() => window.history.back()} userEmail={userEmail} setUserEmail={handleSetUserEmail} onStart={() => navigateTo("dates")} />
      )}

      {currentPage === "dates" && (
        <DatesPage onNext={() => navigateTo("cities")} dates={dates} setDates={setDates} />
      )}

      {currentPage === "cities" && (
        <CitiesPage onNext={() => navigateTo("traveler")} onBack={() => navigateTo("dates")} dates={dates} setDates={setDates}
          selectedCities={selectedCities} setSelectedCities={setSelectedCities}
          startOver={startOver}
          selectedVibes={selectedVibes} />
      )}

      {currentPage === "traveler" && (
        <TravelerPage onNext={() => navigateTo("style")} onBack={() => navigateTo("cities")}
          travelerType={travelerType} setTravelerType={setTravelerType}
          kidAges={kidAges} setKidAges={setKidAges}
          isFirstTimer={isFirstTimer} setIsFirstTimer={setIsFirstTimer}
          startOver={startOver} />
      )}

      {currentPage === "style" && (
        <StylePage onNext={() => navigateTo("vibes")} onBack={() => navigateTo("traveler")}
          dayStartLabel={dayStartLabel} setDayStartLabel={setDayStartLabel}
          dayEndLabel={dayEndLabel} setDayEndLabel={setDayEndLabel}
          tripPace={tripPace} setTripPace={setTripPace}
          startOver={startOver} />
      )}

      {currentPage === "vibes" && (
        <VibesPage onNext={() => navigateTo("practical")} onBack={() => navigateTo("style")}
          selectedVibes={selectedVibes} setSelectedVibes={setSelectedVibes}
          startOver={startOver} />
      )}

      {currentPage === "practical" && (
        <PracticalPage onNext={() => navigateTo("input")} onBack={() => navigateTo("vibes")}
          practical={practical} setPractical={setPractical} dates={dates} startOver={startOver} travelerType={travelerType} />
      )}

      {currentPage === "input" && (
        <InputPage onNext={() => {
            // Go straight to Summary — SummaryPage now runs stage1+stage2 itself
            navigateTo("summary");
          }} onBack={() => navigateTo("practical")}
          userInput={userInput} setUserInput={setUserInput} startOver={startOver}
          selectedCities={selectedCities} setSelectedCities={setSelectedCities} navigateTo={navigateTo}
          onSkipToMenu={() => navigateTo("catalog")} />
      )}

      {currentPage === "summary" && (
        <SummaryPage onNext={(target) => navigateTo(target || "catalog")} onBack={() => navigateTo("input")}
          userInput={userInput} extractedPlaces={extractedPlaces} setExtractedPlaces={setExtractedPlaces}
          selectedPlaces={selectedPlaces} setSelectedPlaces={setSelectedPlaces}
          selectedCities={selectedCities} dates={dates} startOver={startOver}
          selectedVibes={selectedVibes}
          tripConfig={tripConfig} setTripConfig={setTripConfig} navigateTo={navigateTo}
          userEmail={userEmail} setUserEmail={handleSetUserEmail}
          sessionToken={sessionToken} onSignIn={() => setShowAuthModal(true)}
          userCredits={userCredits} setUserCredits={setUserCredits}
          tripPace={tripPace}
          travelerType={travelerType} kidAges={kidAges} isFirstTimer={isFirstTimer}
          dayStartLabel={dayStartLabel} dayEndLabel={dayEndLabel}
          profilerBrief={profilerBrief} setProfilerBrief={setProfilerBrief}
          curatorBrief={curatorBrief} setCuratorBrief={setCuratorBrief}
          aiAnalyses={aiAnalyses} setAiAnalyses={setAiAnalyses}
          previewPicks={previewPicks} setPreviewPicks={setPreviewPicks}
          dayBrief={dayBrief} setDayBrief={setDayBrief}
          questionAnswers={questionAnswers} setQuestionAnswers={setQuestionAnswers}
          practical={practical} draftId={draftId}
          agendaStep={agendaStep} onAgenda={(run) => { setAgendaRun(run); navigateTo("agenda"); }}
          agendaDays={agendaRun?.agenda ? agendaSaved?.days || null : null} onOpenAgenda={() => navigateTo("agenda")}
          writeNow={writeNow} clearWriteNow={() => setWriteNow(false)}
          payNow={payNow} clearPayNow={() => setPayNow(false)} refundAckText={refundAckText} />
      )}

      {/* the agenda: their days before a word is written (agents/agenda); free, its shape with the detail locked */}
      {currentPage === "agenda" && agendaRun?.agenda && (
        <AgendaPage data={agendaRun.agenda} onBack={() => navigateTo("summary")}
          saved={agendaSaved} onSave={setAgendaSaved}
          selected={selectedKeys} listed={listedKeys}
          toPlace={toPlaceOf(agendaSaved?.days, agendaSaved?.tray, extractedPlaces, selectedPlaces, previewPicks)}
          onTakeOut={(place) => setSelectedPlaces((names) => (names || []).filter((n) => String(n).toLowerCase() !== placeKey(place)))}
          onBring={(place) => {
            const name = place.pick || place.name;
            if (!(extractedPlaces || []).some((p) => placeKey(p) === placeKey(place))) {
              setExtractedPlaces([...(extractedPlaces || []), { name, city: place.city || "", neighborhood: place.area || "",
                category: place.category === "food" ? "foodie" : place.category === "hidden_gem" ? "hidden" : (place.category || ""),
                description: "", why: "", image: place.image || "", duration: (place.minutes || 90) / 60, from_catalog: true }]);
            }
            setSelectedPlaces((names) => ((names || []).some((n) => String(n).toLowerCase() === placeKey(place)) ? names : [...(names || []), name]));
          }}
          onGenerate={() => { setWriteNow(true); navigateTo("summary"); }} />
      )}
      {currentPage === "agenda" && !agendaRun?.agenda && agendaRun?.shape && (
        <AgendaShape shape={agendaRun.shape} onBack={() => navigateTo("summary")} onUnlock={() => { setPayNow(true); navigateTo("summary"); }} />
      )}
      {currentPage === "agenda" && !agendaRun && <AgendaLost onLost={() => navigateTo("summary")} />}

      {currentPage === "catalog" && (
        <CatalogPage onNext={() => navigateTo("summary")} onBack={() => navigateTo("summary")}
          selectedPlaces={selectedPlaces} setSelectedPlaces={setSelectedPlaces}
          extractedPlaces={extractedPlaces} setExtractedPlaces={setExtractedPlaces}
          dates={dates} selectedCities={selectedCities} startOver={startOver}
          curatorBrief={curatorBrief} />
      )}

      {currentPage === "result" && (
        <ResultPage tripConfig={tripConfig} navigateTo={navigateTo} startOver={startOver} onSaveTrip={() => saveCurrentTrip()} hasCompanion={hasCompanion} userEmail={userEmail} setUserEmail={handleSetUserEmail} setTripConfig={setTripConfig} sessionToken={sessionToken} />
      )}

      {currentPage === "companion" && (
        <CompanionPage tripConfig={tripConfig} navigateTo={navigateTo} startOver={startOver}
          sessionToken={sessionToken} setTripConfig={setTripConfig} />
      )}

      {currentPage === "terms" && (
        <TermsPage navigateTo={navigateTo} />
      )}

      {currentPage === "privacy" && (
        <PrivacyPage navigateTo={navigateTo} />
      )}

      {currentPage === "refund-policy" && (
        <RefundPolicyPage navigateTo={navigateTo} />
      )}

      <Footer navigateTo={navigateTo} />
    </div>
  );
}

export default App;
